import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, errMsg, CATEGORIES, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge, DataTable, Field, PageHeader, Select } from "@/components/common";
import Modal from "@/components/Modal";

const EMPTY = { name: "", siret: "", vat_number: "", address: "", contact_name: "", contact_email: "", contact_phone: "", payment_terms: "30FDM", category: "materiel", account: "", active: true, notes: "" };

function validate(f) {
  const e = {};
  if (!f.name.trim()) e.name = "La raison sociale est obligatoire";
  if (!/^\d{14}$/.test(f.siret.replace(/\s/g, ""))) e.siret = "Le SIRET doit comporter exactement 14 chiffres";
  if (f.contact_email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.contact_email)) e.contact_email = "Adresse email invalide";
  if (f.account && !/^401/.test(f.account)) e.account = "Le compte fournisseur doit commencer par 401";
  return e;
}

export function SupplierForm({ initial, onSaved }) {
  const { settings } = useAuth();
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [errors, setErrors] = useState({});
  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(f);
    setErrors(e);
    if (Object.keys(e).length) return;
    try {
      const body = { ...f, siret: f.siret.replace(/\s/g, "") };
      const r = f.id ? await api.put(`/suppliers/${f.id}`, body) : await api.post("/suppliers", body);
      toast.success(f.id ? "Fournisseur mis à jour" : "Fournisseur créé");
      onSaved(f.id ? f : r.data);
    } catch (err) { toast.error(errMsg(err)); }
  };
  const inp = (k, label, props = {}) => <Field label={label} error={errors[k]}><input className="inp" value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`supplier-${k}-input`} {...props} /></Field>;
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="supplier-form">
      <div className="grid gap-4 sm:grid-cols-2">
        {inp("name", "Raison sociale *")}
        {inp("siret", "SIRET (14 chiffres) *", { inputMode: "numeric" })}
        {inp("vat_number", "N° TVA intracommunautaire")}
        <Field label="Catégorie"><Select value={f.category} onChange={(v) => set("category", v)} options={Object.entries(CATEGORIES).map(([value, label]) => ({ value, label }))} testid="supplier-category-select" /></Field>
        {inp("address", "Adresse", { className: "inp" })}
        {inp("account", "Compte fournisseur (401xxx)", { placeholder: "Attribué automatiquement si vide" })}
        {inp("contact_name", "Contact principal")}
        {inp("contact_email", "Email du contact", { type: "text" })}
        {inp("contact_phone", "Téléphone")}
        <Field label="Conditions de paiement"><Select value={f.payment_terms} onChange={(v) => set("payment_terms", v)} options={(settings?.payment_terms || []).map((t) => ({ value: t.code, label: t.label }))} testid="supplier-terms-select" /></Field>
        <Field label="Notes" className="sm:col-span-2"><textarea className="inp" rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} data-testid="supplier-active-checkbox" /> Fournisseur actif</label>
      </div>
      <div className="flex justify-end"><button className="btn-primary" data-testid="supplier-save-btn">Enregistrer</button></div>
    </form>
  );
}

export default function Suppliers() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState("");
  const load = () => api.get("/suppliers").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const columns = [
    { key: "name", label: "Raison sociale", render: (r) => <span className="font-semibold">{r.name}</span> },
    { key: "siret", label: "SIRET", render: (r) => <span className="mono text-xs">{r.siret}</span> },
    { key: "category", label: "Catégorie", value: (r) => CATEGORIES[r.category] },
    { key: "account", label: "Compte", render: (r) => <span className="mono text-xs">{r.account}</span> },
    { key: "contact_name", label: "Contact" },
    { key: "contact_email", label: "Email" },
    { key: "active", label: "Statut", value: (r) => (r.active ? "Actif" : "Inactif"), render: (r) => <Badge meta={r.active ? ["Actif", "green"] : ["Inactif", "gray"]} /> },
    { key: "total_spent", label: "Total dépensé TTC", type: "money" },
  ];
  return (
    <div>
      <PageHeader title="Fournisseurs & prestataires" subtitle="Référentiel des fournisseurs, comptes 401 et coordonnées.">
        {can(user, "createur") && <button className="btn-primary" onClick={() => setOpen(true)} data-testid="new-supplier-btn"><Plus size={16} /> Nouveau fournisseur</button>}
      </PageHeader>
      <DataTable columns={columns} rows={rows.filter((r) => !cat || r.category === cat)} title="Fournisseurs" testid="suppliers-table" onRowClick={(r) => nav(`/fournisseurs/${r.id}`)}
        filters={<Select className="!w-44" value={cat} onChange={setCat} placeholder="Toutes catégories" options={Object.entries(CATEGORIES).map(([value, label]) => ({ value, label }))} testid="suppliers-category-filter" />} />
      <Modal open={open} onClose={() => setOpen(false)} title="Nouveau fournisseur" wide testid="new-supplier-modal">
        <SupplierForm initial={{}} onSaved={() => { setOpen(false); load(); }} />
      </Modal>
    </div>
  );
}
